import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, srcForCover, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { canSeeMetrics, hasAccess, canNudge, canNudgeAll, formatDate } from "@/lib/utils2";
import EpisodeRow from "@/components/EpisodeRow";
import StreakBoard from "@/components/StreakBoard";
import StreakCalendar from "@/components/StreakCalendar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Play, Radio, Newspaper, MessageSquareHeart, LifeBuoy, Users, BarChart3,
  Sparkles, Headphones, Star, Megaphone,
} from "lucide-react";

function QuickLaunch({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid} className="lift rounded-2xl border border-brand-line bg-white p-5 flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-brand-sidebar flex items-center justify-center">
        <Icon className="w-5 h-5 text-brand" />
      </div>
      <span className="font-semibold text-brand-ink">{label}</span>
    </Link>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-brand-line bg-white p-5">
      <p className="text-3xl font-display text-brand-ink">{value}</p>
      <p className="text-sm text-brand-muted mt-1">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { load, toggle, current, playing } = usePlayer();
  const [latest, setLatest] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [justNudged, setJustNudged] = useState(false);

  const refresh = async () => {
    const [lat, eps] = await Promise.all([
      api.get("/episodes/latest"),
      api.get("/episodes"),
    ]);
    setLatest(lat.data.episode);
    setEpisodes(eps.data.episodes);
    if (lat.data.episode && !current) load(lat.data.episode);
    setLoaded(true);
  };

  useEffect(() => {
    refresh();
    if (canSeeMetrics(user)) api.get("/metrics").then(({ data }) => setMetrics(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const previous = episodes.filter((e) => !latest || e.id !== latest.id);
  const isLatestPlaying = current && latest && current.id === latest.id && playing;

  const nudge = async (scope) => {
    try {
      const { data } = await api.post(`/episodes/${latest.id}/nudge?scope=${scope}`);
      if (data.downgraded) toast.success("Nudge sent to your store (all-cafes is manager-only) 📣");
      else toast.success(data.scope === "all" ? "Nudge sent to all cafes 📣" : "Nudge sent to your store 📣");
      setJustNudged(true);
      setTimeout(() => setJustNudged(false), 60 * 60 * 1000);
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 429) setJustNudged(true);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">
      <div className="mb-8 animate-fade-up">
        <p className="text-sm text-brand-muted">Welcome back,</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-ink">{user.first_name} {user.last_name}</h1>
      </div>

      {/* Featured latest episode */}
      {!loaded ? (
        <div className="rounded-3xl border border-brand-line bg-white overflow-hidden mb-10">
          <div className="grid md:grid-cols-[220px_1fr]">
            <Skeleton className="h-52 md:h-full w-full" />
            <div className="p-8 space-y-3">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-11 w-32 rounded-full mt-4" />
            </div>
          </div>
        </div>
      ) : latest ? (
        <div className="rounded-3xl border border-brand-line bg-white overflow-hidden card-shadow mb-10 animate-fade-up" data-testid="featured-episode">
          <div className="grid md:grid-cols-[220px_1fr]">
            <div className="relative h-52 md:h-full">
              <img src={srcForCover(latest)} alt="" className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="p-6 sm:p-8">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand uppercase tracking-widest">
                <Sparkles className="w-3.5 h-3.5" /> Latest Episode
              </span>
              <h2 className="font-display text-3xl text-brand-ink mt-3">{latest.title}</h2>
              <p className="text-brand-muted mt-2 leading-relaxed line-clamp-3">{latest.description}</p>
              <p className="text-sm text-brand-muted mt-3">{latest.hosts?.join(" & ")} · {formatDate(latest.publish_at)}</p>
              <div className="flex items-center gap-3 mt-5">
                <Button data-testid="play-latest-button" onClick={() => toggle(latest)} className="rounded-full brand-btn px-6 h-11">
                  <Play className="w-4 h-4 mr-2" /> {isLatestPlaying ? "Playing" : "Play now"}
                </Button>
                {latest.listened && <span className="inline-flex items-center gap-1.5 text-sm text-brand"><Headphones className="w-4 h-4" /> Listened</span>}
                {latest.favorite && <Star className="w-4 h-4 text-brand-dough fill-brand-dough" />}
                {canNudge(user) && (
                  canNudgeAll(user) ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" disabled={justNudged} className="rounded-full border-brand-line h-11 disabled:opacity-60" data-testid="nudge-button">
                          <Megaphone className="w-4 h-4 mr-2" /> {justNudged ? "Nudged" : "Nudge"}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="border-brand-line">
                        <DropdownMenuItem onClick={() => nudge("my_store")} data-testid="nudge-my-store">Nudge my store</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => nudge("all")} data-testid="nudge-all">Nudge all cafes</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Button variant="outline" onClick={() => nudge("my_store")} disabled={justNudged} className="rounded-full border-brand-line h-11 disabled:opacity-60" data-testid="nudge-button">
                      <Megaphone className="w-4 h-4 mr-2" /> {justNudged ? "Nudged this hour" : "Nudge my store"}
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      ) : loaded ? (
        <div className="rounded-3xl border border-dashed border-brand-line bg-white p-10 text-center mb-10">
          <Radio className="w-8 h-8 text-brand-muted mx-auto" />
          <p className="font-display text-2xl text-brand-ink mt-3">No episodes yet</p>
          <p className="text-brand-muted mt-1">New coaching episodes will appear here the moment they publish.</p>
        </div>
      ) : null}

      {/* Quick launch */}
      <h3 className="font-display text-2xl text-brand-ink mb-4">Quick launch</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
        <QuickLaunch to="/app/podcasts" icon={Radio} label="Podcasts" testid="ql-podcasts" />
        <QuickLaunch to="/app/articles" icon={Newspaper} label="Articles" testid="ql-articles" />
        <QuickLaunch to="/app/feedback" icon={MessageSquareHeart} label="Feedback" testid="ql-feedback" />
        <QuickLaunch to="/app/help-desk" icon={LifeBuoy} label="Help Desk" testid="ql-helpdesk" />
        {hasAccess(user, "admin") && <QuickLaunch to="/app/users" icon={Users} label="Users" testid="ql-users" />}
        {canSeeMetrics(user) && <QuickLaunch to="/app/metrics" icon={BarChart3} label="Metrics" testid="ql-metrics" />}
      </div>

      {/* Metrics snapshot */}
      {canSeeMetrics(user) && metrics && (
        <div className="mb-10" data-testid="dashboard-metrics">
          <h3 className="font-display text-2xl text-brand-ink mb-4">At a glance</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Team members" value={metrics.cafe_user_stats.total_users} />
            <Stat label="Published episodes" value={metrics.podcast_stats.published} />
            <Stat label="Total listens" value={metrics.podcast_stats.total_listens} />
            <Stat label="Open tickets" value={metrics.engagement_stats.open_tickets} />
          </div>
        </div>
      )}

      {/* Streaks & leaderboard */}
      <h3 className="font-display text-2xl text-brand-ink mb-4">Your progress</h3>
      <div className="mb-10">
        <StreakBoard />
        <div className="mt-6">
          <StreakCalendar />
        </div>
      </div>

      {/* Previous episodes */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-2xl text-brand-ink">Previous episodes</h3>
        <Link to="/app/podcasts" className="text-sm text-brand font-semibold hover:underline">View all</Link>
      </div>
      <div className="rounded-2xl border border-brand-line bg-brand-sidebar/40 p-2 sm:p-3" data-testid="previous-episodes">
        {loaded && previous.length === 0 && <p className="text-brand-muted text-sm p-6 text-center">No previous episodes yet.</p>}
        {!loaded && [1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4">
            <Skeleton className="h-14 w-14 rounded-lg" />
            <div className="flex-1 space-y-2"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-3 w-1/3" /></div>
          </div>
        ))}
        {previous.map((ep) => (
          <EpisodeRow key={ep.id} episode={ep} onFavoriteToggle={refresh} />
        ))}
      </div>
    </div>
  );
}
